import { zodResolver } from "@hookform/resolvers/zod";
import { Button, Checkbox, Group } from "@mantine/core";
import { createFileRoute, Outlet, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { z } from "zod";

import { useDriver } from "@/contexts";
import { useLoader } from "@/contexts/loader";
import { useSession } from "@/hooks/@custom/useSession";
import { GLOBAL_SCHEMA } from "@/lib/zod";
import { driverService } from "@/services/driver";
import { queueService } from "@/services/queue";
import { useTermService } from "@/services/useTerm";

export type ContactsOutletContext = {
  data: {
    acceptedContacts: Schema["contacts"];
    terms: Partial<Pick<NextApp.Driver["authorization"][0]["useTerm"], "id" | "updatedAt">>;
    driver: NextApp.Driver | null;
    hasDriver?: boolean;
  };
  isLoading?: boolean;
  isError?: boolean;
};

type Schema = z.infer<typeof schema>;
const schema = z.object({
  contacts: GLOBAL_SCHEMA.OPTIONAL.CONTACT,
  terms: GLOBAL_SCHEMA.OPTIONAL.TERMS
});

export const Route = createFileRoute("/app/perfil")({
  component: ProfileLayout,
  loader: async ({ context }) => {
    const store = context.store.getState();
    const { auth } = store;

    const driverId = auth.driver?.id;
    const defaultBody: Schema = {
      contacts: [],
      terms: {
        id: undefined,
        updatedAt: undefined
      }
    };
    let defaultData: ContactsOutletContext["data"] = {
      acceptedContacts: [],
      terms: {
        id: undefined,
        updatedAt: undefined
      },
      driver: null,
      hasDriver: false
    };
    const defaultContacts: Schema["contacts"] = [];

    if (!driverId) return { body: defaultBody, data: defaultData };

    const promises = [driverService.query.getDriverById(+driverId!), queueService.query.getHasDriverByDriverId(+driverId)] as const;
    const [{ data: driverData }, { data: hasDriverData }] = await Promise.all(promises);

    const authorized = driverData?.authorization[0];
    const newContacts: Schema["contacts"] = [];
    if (authorized?.hasEmail) newContacts.push("email");
    if (authorized?.hasSMS) newContacts.push("sms");
    if (authorized?.hasWhats) newContacts.push("whats");

    defaultBody.contacts = newContacts.length > 0 ? newContacts : defaultContacts;
    defaultBody.terms = {
      id: authorized?.useTerm?.id,
      updatedAt: authorized?.updatedAt
    };

    defaultData = {
      acceptedContacts: defaultBody.contacts,
      terms: defaultBody.terms,
      driver: driverData,
      hasDriver: hasDriverData?.hasDriver ?? false
    };

    return { body: defaultBody, data: defaultData };
  },
  onCatch: (error) => {
    console.error("Error fetching driver data:", error);
    toast.error("Erro ao buscar métodos de contato.");
  }
});

export function ProfileLayout() {
  const navigate = useNavigate();
  const { driver } = useSession();

  const { setLoading } = useLoader();
  const { driverQueuePosition } = useDriver();

  const driverId = driver?.id;
  const [defaultData, setDefaultData] = useState<ContactsOutletContext>({
    data: {
      acceptedContacts: [],
      terms: {
        id: undefined,
        updatedAt: undefined
      },
      driver: {
        id: 0,
        name: "",
        cpf: "",
        cnh: "",
        birthDate: "",
        isEnabled: false,
        contact: [],
        shippingCompany: null,
        truck: [],
        company: {
          id: 0,
          name: "",
          cnpj: "",
          company: "",
          uf: "",
          street: "",
          number: "",
          complement: "",
          city: "",
          district: "",
          zipCode: "",
          status: false,
          website: "",
          logo: ""
        },
        authorization: []
      }
    },
    isLoading: false,
    isError: false
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting, isLoading }
  } = useForm<Schema>({
    mode: "all",
    defaultValues: async () => {
      const defaultData: Schema = {
        contacts: [],
        terms: {
          id: undefined,
          updatedAt: undefined
        }
      };
      const defaultContacts: Schema["contacts"] = [];

      setDefaultData((prev) => ({
        ...prev,
        isLoading: true
      }));
      try {
        const promises = [driverService.query.getDriverById(+driverId!), queueService.query.getHasDriverByDriverId(+driverId!)] as const;
        const [driverResponse, hasDriverResponse] = await Promise.all(promises);

        const hasDriverData = hasDriverResponse.data as NextApp.QueueHasDriver;
        const driverData = driverResponse.data as NextApp.Driver;

        const authorized = driverData?.authorization[0];
        const newContacts: Schema["contacts"] = [];
        if (authorized?.hasEmail) newContacts.push("email");
        if (authorized?.hasSMS) newContacts.push("sms");
        if (authorized?.hasWhats) newContacts.push("whats");

        const body: Schema = {
          contacts: newContacts.length > 0 ? newContacts : defaultContacts,
          terms: {
            id: authorized?.useTerm?.id,
            updatedAt: authorized?.updatedAt
          }
        };

        setDefaultData((prev) => ({
          ...prev,
          data: {
            acceptedContacts: body.contacts,
            terms: body.terms,
            driver: driverData,
            hasDriver: hasDriverData.hasDriver ?? false
          }
        }));

        return body;
      } catch (error) {
        console.error("Error fetching driver data:", error);
        toast.error("Erro ao buscar métodos de contato.");
        setDefaultData((prev) => ({ ...prev, isError: true }));

        return defaultData;
      } finally {
        setDefaultData((prev) => ({ ...prev, isLoading: false }));
      }
    },
    resolver: zodResolver(schema)
  });

  const onSubmit: SubmitHandler<Schema> = async (data) => {
    const { contacts, terms } = data;

    const body = {
      hasEmail: contacts.includes("email"),
      hasSMS: contacts.includes("sms"),
      hasWhats: contacts.includes("whats"),
      driver: {
        id: Number(driverId)
      },
      useTerm: {
        id: terms.id,
        updatedAt: terms.updatedAt
      }
    };

    await useTermService.mutation.editAcceptedContatcMethods(body);

    navigate({ to: "/app", replace: true });
  };

  useEffect(() => {
    if (isLoading) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [isLoading]);

  if (isLoading) return null;

  // if (driverQueuePosition?.isDriverActionsBlocked || defaultData.data.hasDriver) {
  if (driverQueuePosition?.isDriverActionsBlocked || defaultData.data.hasDriver) {
    return (
      <form onSubmit={handleSubmit(onSubmit)} className="flex max-w-md flex-col gap-4 rounded-xl p-4 shadow-md">
        <div className="flex flex-col items-center justify-center gap-2 text-center">
          <h1 className="text-2xl font-bold text-gray-800">Autorizar métodos de contato</h1>
          <p className="text-gray-600">Você está na fila e não pode editar seu perfil. Apenas é possível editar os métodos de contato que deseja utilizar.</p>
        </div>

        <Controller
          name="contacts"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <Checkbox.Group
              label="Métodos de contato"
              description="Selecione os métodos de contato que deseja autorizar"
              className="rounded-md bg-gray-300/25 p-2 shadow-sm"
              error={error?.message}
              {...field}
            >
              <Group mt={"xs"}>
                <Checkbox label="Email" value="email" />
                <Checkbox label="SMS" value="sms" />
                <Checkbox label="WhatsApp" value="whats" />
              </Group>
            </Checkbox.Group>
          )}
        />

        <Button type="submit" loading={isSubmitting} fullWidth>
          Salvar
        </Button>
      </form>
    );
  }

  return <Outlet />;
}
